/**
 * Zugriff auf die Kräfte-Management-Daten eines Einsatzes.
 * Datenquelle: `kraefte_management.data` (siehe fuehrung-api).
 */

export interface KMZeit { beginn: string; ende: string }

export interface KMBesatzung { nutzerId?: number | null; funktion?: string; beginn?: string; ende?: string }
export interface KMBlock { key?: string; fahrzeugId?: number | null; besatzung?: KMBesatzung[] }
export interface KMReserveEintrag { nutzerId: number; verfuegbarAb?: string; beginn?: string; ende?: string }

export interface KMTrupp { key: string; name: string; typ: string; fuehrerId: number | null; mitglieder: number[] }
export interface KMTruppProtokoll {
  zeit: string
  aktion: 'GEBILDET' | 'AUFGELOEST' | 'HINZU' | 'ENTFERNT' | 'FUEHRER' | 'UMBENANNT' | 'TYP'
  truppKey: string
  truppName: string
  nutzerId?: number
  alt?: string | number | null
  neu?: string | number | null
}

export interface KMData {
  /** Trupps des Einsatzes (nur aktuelle). Wird vom Kräfte Management gepflegt; die Einsatzverwaltung schickt sie nicht mit. */
  trupps?: KMTrupp[]
  /** Änderungsprotokoll der Trupps – nur serverseitig geschrieben. */
  truppProtokoll?: KMTruppProtokoll[]
  bloecke?: KMBlock[]
  reserveWache?: number[]
  einsatzReserve?: KMReserveEintrag[]
  /** Kräfte im Bereich „Einsatzende“ (beendet bzw. im Abschluss) – gehören weiterhin zum Einsatz. */
  einsatzEnde?: KMReserveEintrag[]
  /** Beginn/Ende je Nutzer – kanonisch für die Anzeige in „Einsatz bearbeiten". */
  zeiten?: Record<string, KMZeit>
}

export interface ZugeordneteKraft {
  nutzerId: number
  bereich: 'besatzung' | 'reserve' | 'einsatzreserve' | 'einsatzende'
  fahrzeugId?: number | null
}

/** Alle dem Einsatz zugeteilten Kräfte (Besatzung, Reserve-Wache, Einsatzreserve, Einsatzende/beendet) – ohne Doppelte. */
export function kmZugeordnete(data: KMData | null | undefined): ZugeordneteKraft[] {
  const d = data ?? {}
  const out: ZugeordneteKraft[] = []
  const seen = new Set<number>()
  const push = (id: number | null | undefined, bereich: ZugeordneteKraft['bereich'], fahrzeugId?: number | null) => {
    if (id == null || seen.has(id)) return
    seen.add(id)
    out.push({ nutzerId: id, bereich, fahrzeugId: fahrzeugId ?? null })
  }
  for (const b of d.bloecke ?? []) for (const r of b.besatzung ?? []) push(r.nutzerId, 'besatzung', b.fahrzeugId)
  for (const id of d.reserveWache ?? []) push(id, 'reserve')
  for (const e of d.einsatzReserve ?? []) push(e.nutzerId, 'einsatzreserve')
  for (const e of d.einsatzEnde ?? []) push(e.nutzerId, 'einsatzende')
  return out
}

/** Beginn/Ende je Kraft. Besatzungszeilen sind die Basis, `zeiten` hat Vorrang. */
export function kmZeiten(data: KMData | null | undefined): Record<number, KMZeit> {
  const d = data ?? {}
  const out: Record<number, KMZeit> = {}
  for (const b of d.bloecke ?? []) for (const r of b.besatzung ?? []) {
    if (r.nutzerId != null) out[r.nutzerId] = { beginn: r.beginn ?? '', ende: r.ende ?? '' }
  }
  for (const [id, z] of Object.entries(d.zeiten ?? {})) {
    if (z) out[Number(id)] = { beginn: z.beginn ?? '', ende: z.ende ?? '' }
  }
  // Einsatzende-Einträge werden im Kräfte Management direkt gepflegt und haben Vorrang
  for (const e of d.einsatzEnde ?? []) {
    out[e.nutzerId] = { beginn: e.beginn ?? '', ende: e.ende ?? '' }
  }
  return out
}

/**
 * Schreibt die Zeiten zurück: eigener `zeiten`-Block **und** Spiegel in die
 * Besatzungszeilen, damit das Kräfte Management dieselben Zeiten zeigt.
 */
export function kmMitZeiten(data: KMData | null | undefined, zeiten: Record<number, KMZeit>): KMData {
  const d: KMData = { ...(data ?? {}) }
  // Trupps und Protokoll nicht zurückschicken: sonst könnte ein veralteter Stand die Trupps überschreiben (Server behält sie)
  delete d.trupps; delete d.truppProtokoll
  d.bloecke = (d.bloecke ?? []).map(b => ({
    ...b,
    besatzung: (b.besatzung ?? []).map(r => {
      if (r.nutzerId == null) return r
      const z = zeiten[r.nutzerId]
      return z ? { ...r, beginn: z.beginn ?? '', ende: z.ende ?? '' } : r
    }),
  }))
  if (d.einsatzEnde) {
    d.einsatzEnde = d.einsatzEnde.map(e => {
      const z = zeiten[e.nutzerId]
      return z ? { ...e, beginn: z.beginn ?? '', ende: z.ende ?? '' } : e
    })
  }
  d.zeiten = Object.fromEntries(
    Object.entries(zeiten).map(([id, z]) => [id, { beginn: z.beginn ?? '', ende: z.ende ?? '' }])
  )
  return d
}

/**
 * Antwortform tolerant auflösen: die API liefert `{einsatz_id, data:{…}}`,
 * je nach Interceptor/Client kann der Payload aber auch direkt vorliegen.
 */
export function holeKmData(res: any): KMData {
  const b = res?.data ?? res ?? {}
  if (b && typeof b === 'object') {
    if ('bloecke' in b || 'reserveWache' in b || 'einsatzReserve' in b) return b as KMData
    const inner = (b as any).data
    if (inner && typeof inner === 'object') return inner as KMData
  }
  return {}
}

/** Trupp einer Person als Text, z. B. „Drohnentrupp 1 (TF)“ bzw. „Drohnentrupp 1“; leer, wenn die Person in keinem Trupp ist. */
export function kmTruppText(data: KMData | null | undefined, nutzerId: number): string {
  const t = (data?.trupps ?? []).find(x => (x.mitglieder ?? []).includes(nutzerId))
  return t ? `${t.name}${t.fuehrerId === nutzerId ? ' (TF)' : ''}` : ''
}
